import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  EmojiEvents,
  TrendingUp,
  Instagram,
  YouTube,
} from '@material-ui/icons';
import { generatePostFormatInsight } from '../lib/insights';
import { campaignTypesFetch, ytCampaignTypesFetch } from 'api_handlers/competitor';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
  },
  highlightBox: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 2,
    background: `linear-gradient(to right, rgba(251, 191, 36, 0.1), rgba(234, 179, 8, 0.1), rgba(251, 191, 36, 0.1))`,
    border: `1px solid rgba(251, 191, 36, 0.3)`,
    marginBottom: theme.spacing(3),
  },
  highlightContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  highlightIcon: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: 'rgba(251, 191, 36, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    paddingBottom: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
  },
  engagementLeaders: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 2,
    background: `linear-gradient(to bottom right, rgba(34, 197, 94, 0.05), rgba(16, 185, 129, 0.05))`,
    border: `1px solid rgba(34, 197, 94, 0.2)`,
    marginBottom: theme.spacing(3),
  },
  leaderGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
  leaderCard: {
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
  },
  brandRow: {
    marginBottom: theme.spacing(3),
  },
  brandHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  brandLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontWeight: 600,
  },
  brandDot: {
    width: 12,
    height: 12,
    borderRadius: '50%',
  },
  progressBar: {
    position: 'relative',
    height: 40,
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    display: 'flex',
    marginBottom: theme.spacing(1.5),
  },
  progressSegment: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    position: 'relative',
    '&:hover': {
      opacity: 1,
    },
  },
  segmentLabel: {
    fontSize: '0.625rem',
    fontWeight: 700,
    color: '#fff',
    zIndex: 1,
  },
  formatBreakdown: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  formatItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.2s',
  },
  formatItemWinner: {
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
    borderColor: 'rgba(34, 197, 94, 0.3)',
  },
  formatItemNormal: {
    backgroundColor: theme.palette.action.hover,
  },
  formatLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  formatDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
  },
  formatStats: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  engagementBadge: {
    fontSize: '0.75rem',
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 12,
  },
  engagementBadgeWinner: {
    backgroundColor: '#16a34a',
    color: '#fff',
  },
  engagementBadgeNormal: {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.secondary,
  },
  insightBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  insightHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  insightText: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
  },
}));

const BRAND_COLORS = ['#f9a620', '#ffd449', '#aab03c', '#548c2f', '#000000'];

const getBrandColor = (index) => {
  return BRAND_COLORS[index % BRAND_COLORS.length];
};

// Calculate engagement rate from API data
const calculateEngagementRate = (likes_count, comments_count, view_count) => {
  const likes = likes_count || 0;
  const comments = comments_count || 0;
  const views = view_count || 0;
  if (views === 0) return 0;
  return ((likes + comments) / views) * 100;
};

export default function PostFormatAnalysis({
  selectedBrands = [],
  competitorBrands = [],
  start_date = null,
  end_date = null,
  showAsPercentage = false,
  platform = 'instagram',
}) {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [formatData, setFormatData] = useState({});
  
  // Determine platform
  const isYouTube = platform === 'yt' || platform === 'youtube';
  const platformName = isYouTube ? 'YouTube' : 'Instagram';
  const defaultFormat = isYouTube ? 'Shorts' : 'Reels';

  // Fetch aggregated-by-type data for each brand
  useEffect(() => {
    const fetchFormatData = async () => {
      if (selectedBrands.length === 0 || competitorBrands.length === 0) {
        setLoading(false);
        return;
      }

      setLoading(true);
      const data = {};
      const isYouTube = platform === 'yt' || platform === 'youtube';

      try {
        for (const brandTitle of selectedBrands) {
          const brandInfo = competitorBrands.find(b => b.title === brandTitle);
          
          // Check for appropriate handle based on platform
          const brandHandle = isYouTube 
            ? (brandInfo?.yt_handle || brandInfo?.handle)
            : (brandInfo?.ig_handle || brandInfo?.handle);
          
          if (!brandInfo || !brandHandle) continue;

          try {
            const resp = isYouTube
              ? await ytCampaignTypesFetch(
                  brandHandle,
                  start_date,
                  end_date,
                  '',
                  ''
                )
              : await campaignTypesFetch(
                  brandHandle,
                  start_date,
                  end_date,
                  '',
                  ''
                );

            if (resp && resp.campaign_type) {
              data[brandTitle] = {};
              
              resp.campaign_type.forEach((item) => {
                let postType = null;
                
                if (isYouTube) {
                  // YouTube: map to Shorts and Video
                  if (item.post_type === 'short' || item.post_type === 'shorts') {
                    postType = 'Shorts';
                  } else if (item.post_type === 'video') {
                    postType = 'Video';
                  }
                } else {
                  // Instagram: map to Reels and Posts
                  if (item.post_type === 'reel') {
                    postType = 'Reels';
                  } else if (item.post_type === 'post') {
                    postType = 'Posts';
                  }
                }
                
                if (postType) {
                  data[brandTitle][postType] = {
                    count: item.count || 0,
                    likes_count: item.likes_count || 0,
                    comments_count: item.comments_count || 0,
                    view_count: item.view_count || 0,
                    creators: item.creators || 0,
                    engagement: calculateEngagementRate(
                      item.likes_count || 0,
                      item.comments_count || 0,
                      item.view_count || 0
                    ),
                  };
                }
              });
            }
          } catch (error) {
            console.error(`Error fetching format data for ${brandTitle}:`, error);
          }
        }

        setFormatData(data);
      } catch (error) {
        console.error('Error fetching format data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFormatData();
  }, [selectedBrands, competitorBrands, start_date, end_date, platform]);

  // Process API data to component format
  const postFormatAnalysisData = useMemo(() => {
    const formats = isYouTube ? ['Shorts', 'Video'] : ['Reels', 'Posts'];
    
    return formats.map((format) => {
      const result = { format, platform: platformName };
      
      // Aggregate data across all brands
      let totalEngagement = 0;
      let brandCount = 0;
      
      selectedBrands.forEach((brand) => {
        const brandData = formatData[brand]; // formatData is the state variable
        if (brandData && brandData[format]) {
          const brandFormatData = brandData[format];
          result[brand] = brandFormatData.count;
          result[`${brand}_engagement`] = brandFormatData.engagement;
          
          totalEngagement += brandFormatData.engagement;
          brandCount++;
        } else {
          result[brand] = 0;
          result[`${brand}_engagement`] = 0;
        }
      });
      
      // Calculate overall average engagement
      result.avgEngagement = brandCount > 0 ? totalEngagement / brandCount : 0;
      
      return result;
    });
  }, [formatData, selectedBrands, isYouTube, platformName]);

  // Find highest engagement format
  const highestEngagement = useMemo(() => {
    return postFormatAnalysisData.reduce((max, format) =>
      format.avgEngagement > max.avgEngagement ? format : max
    , postFormatAnalysisData[0] || { format: defaultFormat, avgEngagement: 0 });
  }, [postFormatAnalysisData, defaultFormat]);

  // Get platform-specific formats
  const platformFormats = useMemo(() => {
    return postFormatAnalysisData.filter(f => f.platform === platformName);
  }, [postFormatAnalysisData, platformName]);

  // Transform data for insight function
  const insightData = useMemo(() => {
    return platformFormats.map((format) => {
      const formatData = { format: format.format };
      selectedBrands.forEach((brand) => {
        formatData[brand] = format[brand] || 0;
      });
      return formatData;
    });
  }, [platformFormats, selectedBrands]);

  if (loading) {
    return (
      <Box className={classes.container} display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      {/* Highest Engagement Format Highlight */}
      <Box className={classes.highlightBox}>
        <Box className={classes.highlightContent}>
          <Box className={classes.highlightIcon}>
            <EmojiEvents style={{ color: '#f59e0b', fontSize: 20 }} />
          </Box>
          <Box style={{ flex: 1 }}>
            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 4 }}>
              Highest Engagement Format
            </Typography>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="h6" style={{ fontWeight: 700, color: '#f59e0b' }}>
                {highestEngagement.format}
              </Typography>
              <Typography variant="caption" style={{ color: '#9ca3af' }}>
                ({platformName})
              </Typography>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                • {highestEngagement.avgEngagement.toFixed(1)}% avg engagement
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Platform Section */}
      <Box>
        <Box className={classes.sectionHeader}>
          {isYouTube ? (
            <YouTube style={{ color: '#ff0000', fontSize: 24 }} />
          ) : (
            <Instagram style={{ color: '#e91e63', fontSize: 24 }} />
          )}
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            {platformName}
          </Typography>
        </Box>

        {/* Engagement Leaders by Brand */}
        <Box className={classes.engagementLeaders}>
          <Box display="flex" alignItems="center" gap={1} marginBottom={1.5}>
            <EmojiEvents style={{ color: '#16a34a', fontSize: 16 }} />
            <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
              Engagement Leaders by Brand
            </Typography>
          </Box>
          <Grid container spacing={1} className={classes.leaderGrid}>
            {selectedBrands.map((brand, brandIndex) => {
              const format1Data = platformFormats.find(f => f.format === (isYouTube ? 'Shorts' : 'Reels'));
              const format2Data = platformFormats.find(f => f.format === (isYouTube ? 'Video' : 'Posts'));
              
              const format1Engagement = format1Data?.[`${brand}_engagement`] || 0;
              const format2Engagement = format2Data?.[`${brand}_engagement`] || 0;
              
              const format1Name = isYouTube ? 'Shorts' : 'Reels';
              const format2Name = isYouTube ? 'Video' : 'Posts';
              const winner = format1Engagement > format2Engagement ? format1Name : format2Name;
              const winnerEngagement = winner === format1Name ? format1Engagement : format2Engagement;
              const brandColor = getBrandColor(brandIndex);

              return (
                <Grid item key={brand} style={{ minWidth: 120 }}>
                  <Box className={classes.leaderCard}>
                    <Box display="flex" alignItems="center" gap={0.5} marginBottom={0.5}>
                      <Box
                        className={classes.brandDot}
                        style={{ backgroundColor: brandColor }}
                      />
                      <Typography variant="caption" style={{ fontWeight: 600, fontSize: '0.625rem' }}>
                        {brand}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="caption" style={{ fontWeight: 700, color: '#16a34a' }}>
                        {winner}
                      </Typography>
                      <Typography variant="caption" style={{ color: '#9ca3af', fontSize: '0.625rem' }}>
                        ({winnerEngagement.toFixed(1)}%)
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Brand Distribution */}
        <Box marginTop={3}>
          <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 2 }}>
            Brand Distribution
          </Typography>
          
          {selectedBrands.map((brand, brandIndex) => {
            const brandTotal = platformFormats.reduce(
              (sum, format) => sum + (format[brand] || 0),
              0
            );
            const brandColor = getBrandColor(brandIndex);

            return (
              <Box key={brand} className={classes.brandRow}>
                <Box className={classes.brandHeader}>
                  <Box className={classes.brandLabel} style={{ color: brandColor }}>
                    <Box
                      className={classes.brandDot}
                      style={{ backgroundColor: brandColor }}
                    />
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      {brand}
                    </Typography>
                  </Box>
                  <Typography variant="caption" style={{ color: '#9ca3af', fontWeight: 500 }}>
                    {brandTotal} posts
                  </Typography>
                </Box>

                {/* Stacked Progress Bar */}
                <Box className={classes.progressBar}>
                  {platformFormats.map((formatData, idx) => {
                    const value = formatData[brand] || 0;
                    const percentage = brandTotal > 0 ? (value / brandTotal) * 100 : 0;
                    if (percentage === 0) return null;

                    return (
                      <Box
                        key={idx}
                        className={classes.progressSegment}
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: brandColor,
                          opacity: idx === 0 ? 0.9 : 0.65,
                        }}
                      >
                        {percentage > 12 && (
                          <Typography className={classes.segmentLabel}>
                            {showAsPercentage ? `${percentage.toFixed(0)}%` : value}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>

                {/* Format Breakdown */}
                <Box className={classes.formatBreakdown}>
                  {platformFormats.map((formatData, idx) => {
                    const value = formatData[brand] || 0;
                    if (value === 0) return null;

                    const format1Name = isYouTube ? 'Shorts' : 'Reels';
                    const format2Name = isYouTube ? 'Video' : 'Posts';
                    const format1Data = platformFormats.find(f => f.format === format1Name);
                    const format2Data = platformFormats.find(f => f.format === format2Name);
                    const format1Engagement = format1Data?.[`${brand}_engagement`] || 0;
                    const format2Engagement = format2Data?.[`${brand}_engagement`] || 0;
                    
                    const isWinner = (formatData.format === format1Name && format1Engagement > format2Engagement) ||
                                    (formatData.format === format2Name && format2Engagement > format1Engagement);
                    
                    const engagement = formatData[`${brand}_engagement`] || 0;

                    return (
                      <Box
                        key={idx}
                        className={isWinner ? classes.formatItemWinner : classes.formatItemNormal}
                      >
                        <Box className={classes.formatLabel}>
                          <Box
                            className={classes.formatDot}
                            style={{
                              backgroundColor: brandColor,
                              opacity: idx === 0 ? 0.9 : 0.65,
                            }}
                          />
                          <Typography variant="caption" style={{ fontWeight: 500 }}>
                            {formatData.format}
                          </Typography>
                          {isWinner && (
                            <EmojiEvents style={{ color: '#16a34a', fontSize: 12 }} />
                          )}
                        </Box>
                        <Box className={classes.formatStats}>
                          <Typography variant="caption" style={{ fontWeight: 600 }}>
                            {value} posts
                          </Typography>
                          <Chip
                            label={`${engagement.toFixed(1)}%`}
                            size="small"
                            className={isWinner ? classes.engagementBadgeWinner : classes.engagementBadgeNormal}
                            style={{
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              height: 20,
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Key Insight */}
      <Box className={classes.insightBox}>
        <Box className={classes.insightHeader}>
          <TrendingUp style={{ color: '#10b981', fontSize: 16 }} />
          <Typography variant="subtitle2" style={{ fontWeight: 500 }}>
            Key Insight
          </Typography>
        </Box>
        <Typography className={classes.insightText}>
          {generatePostFormatInsight(insightData)}
        </Typography>
      </Box>
    </Box>
  );
}

